/**
 * @summary Get SP Lot Workflow Details
 */
export const getSPLotWorkflowDetails = (data: { splot_ids: string[] }, options?: AxiosRequestConfig): Promise<AxiosResponse<any>> => {
    return axios.post(`/api/v1/splots-workflow-details`, data, options);
};

export const useGetSPLotWorkflowDetails = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getSPLotWorkflowDetails>>, TError, { data: { splot_ids: string[] } }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};
    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getSPLotWorkflowDetails>>, { data: { splot_ids: string[] } }> = (props) => {
        const { data } = props ?? {};
        return getSPLotWorkflowDetails(data, axiosOptions);
    };
    return useMutation<Awaited<ReturnType<typeof getSPLotWorkflowDetails>>, TError, { data: { splot_ids: string[] } }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Validate Scanned Material For SP Lot
 */
export const validateScannedMaterialForSplot = (
    data: { matid: string },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<any>> => {
    return axios.get(`/api/v1/material-validation/${encodeURIComponent(data.matid)}`, options);
};

export const useValidateScannedMaterialForSplot = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof validateScannedMaterialForSplot>>, TError, { data: { matid: string } }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};
    const mutationFn: MutationFunction<Awaited<ReturnType<typeof validateScannedMaterialForSplot>>, { data: { matid: string } }> = (props) => {
        const { data } = props ?? {};
        return validateScannedMaterialForSplot(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof validateScannedMaterialForSplot>>, TError, { data: { matid: string } }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Report No Seed for Materials
 */
export const reportNoSeedForMaterials = (
    data: {
        matid: string;
        remark_id: number;
        another_harvest_required: boolean;
        user_id: number;
    },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<any>> => {
    return axios.post(`/api/v1/report-no-seed-for-materials`, data, options);
};

export const useReportNoSeedForMaterials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof reportNoSeedForMaterials>>, TError, {
        data: {
            matid: string;
            remark_id: number;
            another_harvest_required: boolean;
            user_id: number;
        }
    }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportNoSeedForMaterials>>, {
        data: {
            matid: string;
            remark_id: number;
            another_harvest_required: boolean;
            user_id: number;
        }
    }> = (props) => {
        const { data } = props ?? {};
        return reportNoSeedForMaterials(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof reportNoSeedForMaterials>>, TError, {
        data: {
            matid: string;
            remark_id: number;
            another_harvest_required: boolean;
            user_id: number;
        }
    }, TContext>(
        mutationFn,
        mutationOptions
    );
};

export const FeedbackType = {
    left_feedback: 'left_feedback',
    request_information: 'request_information',
    report_issue: 'report_issue',
} as const;

export type FeedbackType = (typeof FeedbackType)[keyof typeof FeedbackType];

export type SubmitFeedbackMutationBody = {
    feedback_type: FeedbackType;
    subject: string;
    description: string;
    user_email: string;
    user_name: string;
    attachments?: File[];
};

/**
 * @summary Submit Feedback
 */
export const submitFeedback = (data: SubmitFeedbackMutationBody, options?: AxiosRequestConfig): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append('feedback_type', data.feedback_type);
    formData.append('subject', data.subject);
    formData.append('description', data.description);
    formData.append('user_email', data.user_email);
    formData.append('user_name', data.user_name);

    (data.attachments ?? []).forEach((file) => {
        formData.append('attachments', file);
    });

    return axios.post(`/api/common/v1/feedback_email`, formData, options);
};

export const useSubmitFeedback = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof submitFeedback>>, TError, { data: SubmitFeedbackMutationBody }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitFeedback>>, { data: SubmitFeedbackMutationBody }> = (props) => {
        const { data } = props ?? {};
        return submitFeedback(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof submitFeedback>>, TError, { data: SubmitFeedbackMutationBody }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Complete Trial
 */
export const completeTrial = (
    data: {
        trial_id: string;
        user_id: number;
    },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<any>> => {
    return axios.post(`/api/v1/trial-completion`, data, options);
};

export const useCompleteTrial = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof completeTrial>>, TError, {
        data: {
            trial_id: string;
            user_id: number;
        }
    }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof completeTrial>>, {
        data: {
            trial_id: string;
            user_id: number;
        }
    }> = (props) => {
        const { data } = props ?? {};
        return completeTrial(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof completeTrial>>, TError, {
        data: {
            trial_id: string;
            user_id: number;
        }
    }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Bulk Update Material Remarks
 */
export const bulkUpdateMaterialRemarks = (
    data: {
        material_ids: string[];
        remark_id: number;
        user_id: number;
    },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<any>> => {
    return axios.post(`/api/v1/material-remarks`, data, options);
};

export const useBulkUpdateMaterialRemarks = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof bulkUpdateMaterialRemarks>>, TError, {
        data: {
            material_ids: string[];
            remark_id: number;
            user_id: number;
        }
    }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof bulkUpdateMaterialRemarks>>, {
        data: {
            material_ids: string[];
            remark_id: number;
            user_id: number;
        }
    }> = (props) => {
        const { data } = props ?? {};
        return bulkUpdateMaterialRemarks(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof bulkUpdateMaterialRemarks>>, TError, {
        data: {
            material_ids: string[];
            remark_id: number;
            user_id: number;
        }
    }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Manually Complete Materials
 */
export const manuallyCompleteMaterials = (
    data: {
        material_ids: string[];
        user_id: number;
    },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<any>> => {
    return axios.post(`/api/v1/manual-material-completion`, data, options);
};

export const useManuallyCompleteMaterials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof manuallyCompleteMaterials>>, TError, {
        data: {
            material_ids: string[];
            user_id: number;
        }
    }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof manuallyCompleteMaterials>>, {
        data: {
            material_ids: string[];
            user_id: number;
        }
    }> = (props) => {
        const { data } = props ?? {};
        return manuallyCompleteMaterials(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof manuallyCompleteMaterials>>, TError, {
        data: {
            material_ids: string[];
            user_id: number;
        }
    }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Export Trial Materials CSV
 */
export const exportTrialMaterialsCsv = (
    data: { trial_ids: string[] },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<Blob>> => {
    return axios.post(`/api/v1/trial-materials-csv`, data, {
        responseType: 'blob',
        ...(options ?? {}),
    });
};

export const useExportTrialMaterialsCsv = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof exportTrialMaterialsCsv>>, TError, { data: { trial_ids: string[] } }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof exportTrialMaterialsCsv>>, { data: { trial_ids: string[] } }> = (props) => {
        const { data } = props ?? {};
        return exportTrialMaterialsCsv(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof exportTrialMaterialsCsv>>, TError, { data: { trial_ids: string[] } }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Export S2S Reports
 */
export const exportS2sReport = (options?: AxiosRequestConfig): Promise<AxiosResponse<Blob>> => {
    return axios.post(`/api/v1/s2s-export`, undefined, {
        responseType: 'blob',
        ...(options ?? {}),
    });
};

export const useExportS2sReport = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof exportS2sReport>>, TError, void, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof exportS2sReport>>, void> = () => {
        return exportS2sReport(axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof exportS2sReport>>, TError, void, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Export S2S Events Reports
 */
export const exportS2sEventsReport = (
    data: { start_date: string; end_date: string; range?: string },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<Blob>> => {
    return axios.post(`/api/v1/s2s-export-events`, data, {
        responseType: 'blob',
        ...(options ?? {}),
    });
};

export const useExportS2sEventsReport = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof exportS2sEventsReport>>, TError, { data: { start_date: string; end_date: string; range?: string } }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof exportS2sEventsReport>>, { data: { start_date: string; end_date: string; range?: string } }> = (props) => {
        const { data } = props ?? {};
        return exportS2sEventsReport(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof exportS2sEventsReport>>, TError, { data: { start_date: string; end_date: string; range?: string } }, TContext>(
        mutationFn,
        mutationOptions
    );
};
/**
 * @summary Add Comment to Workflow Step
 */
export const addWorkflowStepComment = (
    data: {
        splot_ids: string[];
        workflow_step_id: number;
        workflow_protocol_id: number;
        comment: string;
        user_id: number;
    },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/splot/step/comment`, data, options);
};

export type AddWorkflowStepCommentMutationResult = NonNullable<Awaited<ReturnType<typeof addWorkflowStepComment>>>;
export type AddWorkflowStepCommentMutationBody = {
    splot_ids: string[];
    workflow_step_id: number;
    workflow_protocol_id: number;
    comment: string;
    user_id: number;
};
export type AddWorkflowStepCommentMutationError = AxiosError<HTTPValidationError>;

export const useAddWorkflowStepComment = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof addWorkflowStepComment>>, TError, { data: AddWorkflowStepCommentMutationBody }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof addWorkflowStepComment>>, { data: AddWorkflowStepCommentMutationBody }> = (props) => {
        const { data } = props ?? {};
        return addWorkflowStepComment(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof addWorkflowStepComment>>, TError, { data: AddWorkflowStepCommentMutationBody }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary Begin Processing Workflow Step
 */
export const beginProcessingWorkflowStep = (
    data: {
        splot_ids: string[];
        workflow_step_id: number;
        workflow_protocol_id: number;
        equipment_id: string;
        skip_reason?: string;
        user_id: number;
        comment: string;
    },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/begin-processing`, data, options);
};

export type BeginProcessingWorkflowStepMutationResult = NonNullable<Awaited<ReturnType<typeof beginProcessingWorkflowStep>>>;
export type BeginProcessingWorkflowStepMutationBody = {
    splot_ids: string[];
    workflow_step_id: number;
    workflow_protocol_id: number;
    equipment_id: string;
    skip_reason?: string;
    user_id: number;
    comment: string;
};
export type BeginProcessingWorkflowStepMutationError = AxiosError<HTTPValidationError>;

export const useBeginProcessingWorkflowStep = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof beginProcessingWorkflowStep>>, TError, { data: BeginProcessingWorkflowStepMutationBody }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof beginProcessingWorkflowStep>>, { data: BeginProcessingWorkflowStepMutationBody }> = (props) => {
        const { data } = props ?? {};
        return beginProcessingWorkflowStep(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof beginProcessingWorkflowStep>>, TError, { data: BeginProcessingWorkflowStepMutationBody }, TContext>(
        mutationFn,
        mutationOptions
    );
};
/**
 * Generated by orval v6.10.0 🍺
 * Do not edit manually.
 * APL API
 * APL API, Clients: APL UI
 * OpenAPI spec version: 0.9
 */
import axios from "axios"
import type { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios"
import { useQuery, useMutation } from "@tanstack/react-query"
import type {
    UseQueryOptions,
    UseMutationOptions,
    QueryFunction,
    MutationFunction,
    UseQueryResult,
    QueryKey,
} from "@tanstack/react-query"
import type {
    AplSiteModel,
    HTTPValidationError,

    RefCropModel,
    RefMintLocationModel,
    RemarkModel,
    SeedProcessingLotModel,
    SeedProcessingLotMaterials,
    SeedProcessingLotWithMaterialCount,
    SelectionListRowModel,
    SearchSelectionListRowParams,
    StatusModel,
    StatusKeyValue,
    TrialMaterialModel,
    TrialSearchParams,
    TreatmentModel1,
    UserModel,
    ModuleDataModel,
    ModuleRolesDataModel,
    SearchUserModel,
    SPLotModel,
    CreateSPLotModel,
    AddMaterialModel,
    CreateFilterSPLotModel,
    FilterSPLotModel,
    GetFiltersModel,
    UpdateSpLotModel,
    CreateLabelContainerModel,
    validateSPLotModel,
    CreateMultipleHarvestModel,
    SearchTrialsModel,
    getInseasonTrialObj
} from "./"
import type { TrailSearchRequestBody } from "./trailSearchRequestBody"
import type { MaterialBody } from "./materialBody"
import type { EditTrialMaterialsRequestBody } from "./editTrialMaterialModel"
import type { LabelTemplate } from "./labelTemplate"

/**
 * @summary Get All Sites
 */
export const getAllSites = (options?: AxiosRequestConfig): Promise<AxiosResponse<AplSiteModel[]>> => {
    return axios.get(`/api/common/v1/sites`, options)
}

export const getGetAllSitesQueryKey = () => [`/api/common/v1/sites`]

export type GetAllSitesQueryResult = NonNullable<Awaited<ReturnType<typeof getAllSites>>>
export type GetAllSitesQueryError = AxiosError<unknown>

export const useGetAllSites = <
    TData = Awaited<ReturnType<typeof getAllSites>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getAllSites>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetAllSitesQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllSites>>> = ({ signal }) =>
        getAllSites({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getAllSites>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Download Upload File
 */
export const downloadUploadFile = (fileId: number, options?: AxiosRequestConfig): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/file-upload/${fileId}/download`, undefined, options)
}

export type DownloadUploadFileMutationResult = NonNullable<Awaited<ReturnType<typeof downloadUploadFile>>>

export type DownloadUploadFileMutationError = AxiosError<HTTPValidationError>

export const useDownloadUploadFile = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof downloadUploadFile>>, TError, { fileId: number }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof downloadUploadFile>>, { fileId: number }> = (
        props
    ) => {
        const { fileId } = props ?? {}

        return downloadUploadFile(fileId, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof downloadUploadFile>>, TError, { fileId: number }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Download Upload File History
 */
export const downloadUploadFileHistory = (
    fileHistoryId: number,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/file-upload/${fileHistoryId}/file-history/download`, undefined, options)
}

export type DownloadUploadFileHistoryMutationResult = NonNullable<Awaited<ReturnType<typeof downloadUploadFileHistory>>>

export type DownloadUploadFileHistoryMutationError = AxiosError<HTTPValidationError>

export const useDownloadUploadFileHistory = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof downloadUploadFileHistory>>,
        TError,
        { fileHistoryId: number },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof downloadUploadFileHistory>>,
        { fileHistoryId: number }
    > = (props) => {
        const { fileHistoryId } = props ?? {}

        return downloadUploadFileHistory(fileHistoryId, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof downloadUploadFileHistory>>,
        TError,
        { fileHistoryId: number },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * If the file exists:
 * delete the file_upload and whatever data is associated with it.
 * @summary Delete File Upload File
 */
export const deleteFileUploadFile = (fileId: number, options?: AxiosRequestConfig): Promise<AxiosResponse<unknown>> => {
    return axios.delete(`/api/v1/file-upload/${fileId}`, options)
}

export type DeleteFileUploadFileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteFileUploadFile>>>

export type DeleteFileUploadFileMutationError = AxiosError<HTTPValidationError>

export const useDeleteFileUploadFile = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteFileUploadFile>>,
        TError,
        { fileId: number },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteFileUploadFile>>, { fileId: number }> = (
        props
    ) => {
        const { fileId } = props ?? {}

        return deleteFileUploadFile(fileId, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof deleteFileUploadFile>>, TError, { fileId: number }, TContext>(
        mutationFn,
        mutationOptions
    )
}


/**
 * @summary Download Upload Template
 */
export const downloadUploadTemplate = (options?: AxiosRequestConfig): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/file-upload/download-template`, undefined, options)
}

export type DownloadUploadTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof downloadUploadTemplate>>>

export type DownloadUploadTemplateMutationError = AxiosError<unknown>

export const useDownloadUploadTemplate = <
    TError = AxiosError<unknown>,
    TVariables = void,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof downloadUploadTemplate>>, TError, TVariables, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof downloadUploadTemplate>>, TVariables> = () => {
        return downloadUploadTemplate(axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof downloadUploadTemplate>>, TError, TVariables, TContext>(
        mutationFn,
        mutationOptions
    )
}
/**
 * @summary Get Health
 */
export const getHealth = (options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
    return axios.get(`/api/v1/health`, options)
}

export const getGetHealthQueryKey = () => [`/api/v1/health`]

export type GetHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getHealth>>>
export type GetHealthQueryError = AxiosError<unknown>

export const useGetHealth = <TData = Awaited<ReturnType<typeof getHealth>>, TError = AxiosError<unknown>>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetHealthQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHealth>>> = ({ signal }) =>
        getHealth({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getHealth>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Delete Material By Seed Processing Lot Id
 */
export const deleteMaterialBySeedProcessingLotId = (
    seedProcessingLotId: number,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(`/api/v1/material/${seedProcessingLotId}/seed-processing-lot-id`, options)
}

export type DeleteMaterialBySeedProcessingLotIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof deleteMaterialBySeedProcessingLotId>>
>

export type DeleteMaterialBySeedProcessingLotIdMutationError = AxiosError<HTTPValidationError>

export const useDeleteMaterialBySeedProcessingLotId = <
    TError = AxiosError<HTTPValidationError>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteMaterialBySeedProcessingLotId>>,
        TError,
        { seedProcessingLotId: number },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof deleteMaterialBySeedProcessingLotId>>,
        { seedProcessingLotId: number }
    > = (props) => {
        const { seedProcessingLotId } = props ?? {}

        return deleteMaterialBySeedProcessingLotId(seedProcessingLotId, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof deleteMaterialBySeedProcessingLotId>>,
        TError,
        { seedProcessingLotId: number },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * @summary Delete Material Container
 */
export const deleteMaterialContainer = (
    materialId: number,
    containerUuid: string,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(`/api/v1/material-container/${materialId}/${containerUuid}`, options)
}

export type DeleteMaterialContainerMutationResult = NonNullable<Awaited<ReturnType<typeof deleteMaterialContainer>>>

export type DeleteMaterialContainerMutationError = AxiosError<HTTPValidationError>

export const useDeleteMaterialContainer = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteMaterialContainer>>,
        TError,
        { materialId: number; containerUuid: string },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof deleteMaterialContainer>>,
        { materialId: number; containerUuid: string }
    > = (props) => {
        const { materialId, containerUuid } = props ?? {}

        return deleteMaterialContainer(materialId, containerUuid, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof deleteMaterialContainer>>,
        TError,
        { materialId: number; containerUuid: string },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * @summary Delete Material Remark By Material Id
 */
export const deleteMaterialRemarkByMaterialId = (
    materialId: number,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(`/api/v1/material-remark/${materialId}`, options)
}

export type DeleteMaterialRemarkByMaterialIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof deleteMaterialRemarkByMaterialId>>
>

export type DeleteMaterialRemarkByMaterialIdMutationError = AxiosError<HTTPValidationError>

export const useDeleteMaterialRemarkByMaterialId = <
    TError = AxiosError<HTTPValidationError>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteMaterialRemarkByMaterialId>>,
        TError,
        { materialId: number },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof deleteMaterialRemarkByMaterialId>>,
        { materialId: number }
    > = (props) => {
        const { materialId } = props ?? {}

        return deleteMaterialRemarkByMaterialId(materialId, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof deleteMaterialRemarkByMaterialId>>,
        TError,
        { materialId: number },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * @summary Delete Material Remark By Material Id And Remark Id
 */
export const deleteMaterialRemarkByMaterialIdAndRemarkId = (
    materialId: number,
    remarkId: number,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(`/api/v1/material-remark/${materialId}/${remarkId}`, options)
}

export type DeleteMaterialRemarkByMaterialIdAndRemarkIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof deleteMaterialRemarkByMaterialIdAndRemarkId>>
>

export type DeleteMaterialRemarkByMaterialIdAndRemarkIdMutationError = AxiosError<HTTPValidationError>

export const useDeleteMaterialRemarkByMaterialIdAndRemarkId = <
    TError = AxiosError<HTTPValidationError>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteMaterialRemarkByMaterialIdAndRemarkId>>,
        TError,
        { materialId: number; remarkId: number },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof deleteMaterialRemarkByMaterialIdAndRemarkId>>,
        { materialId: number; remarkId: number }
    > = (props) => {
        const { materialId, remarkId } = props ?? {}

        return deleteMaterialRemarkByMaterialIdAndRemarkId(materialId, remarkId, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof deleteMaterialRemarkByMaterialIdAndRemarkId>>,
        TError,
        { materialId: number; remarkId: number },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * Get all Crops for drop downs
 * @summary Get Ref Crops
 */
export const getRefCrops = (siteCode?: string, options?: AxiosRequestConfig): Promise<AxiosResponse<RefCropModel[]>> => {
    const url = siteCode 
        ? `/api/common/v1/crops_by_site_code?site_code=${siteCode}`
        : '/api/common/v1/crops';
    return axios.get(url, options)
}

export const getGetRefCropsQueryKey = (siteCode?: string) => [`/api/common/v1/crops_by_site_code`, siteCode]

export type GetRefCropsQueryResult = NonNullable<Awaited<ReturnType<typeof getRefCrops>>>
export type GetRefCropsQueryError = AxiosError<unknown>

export function useGetRefCrops<
    TData = Awaited<ReturnType<typeof getRefCrops>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRefCrops>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey }

export function useGetRefCrops<
    TData = Awaited<ReturnType<typeof getRefCrops>>,
    TError = AxiosError<unknown>,
>(siteCode: string | undefined, options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRefCrops>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey }

export function useGetRefCrops<
    TData = Awaited<ReturnType<typeof getRefCrops>>,
    TError = AxiosError<unknown>,
>(siteCodeOrOptions?: string | undefined | {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRefCrops>>, TError, TData>
    axios?: AxiosRequestConfig
}, maybeOptions?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRefCrops>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
    let siteCode: string | undefined
    let options: {
        query?: UseQueryOptions<Awaited<ReturnType<typeof getRefCrops>>, TError, TData>
        axios?: AxiosRequestConfig
    } | undefined

    // Handle both calling patterns: useGetRefCrops(options?) or useGetRefCrops(siteCode?, options?)
    if (typeof siteCodeOrOptions === 'string' || siteCodeOrOptions === undefined) {
        siteCode = siteCodeOrOptions as string | undefined
        options = maybeOptions
    } else {
        siteCode = undefined
        options = siteCodeOrOptions
    }

    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetRefCropsQueryKey(siteCode)

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRefCrops>>> = ({ signal }) =>
        getRefCrops(siteCode, { signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getRefCrops>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}



/**
 * @summary Get Ref Locations
 */
export const getRefLocations = (options?: AxiosRequestConfig): Promise<AxiosResponse<RefMintLocationModel[]>> => {
    return axios.get(`/api/v1/ref/locations`, options)
}

export const getGetRefLocationsQueryKey = () => [`/api/v1/ref/locations`]

export type GetRefLocationsQueryResult = NonNullable<Awaited<ReturnType<typeof getRefLocations>>>
export type GetRefLocationsQueryError = AxiosError<unknown>

export const useGetRefLocations = <
    TData = Awaited<ReturnType<typeof getRefLocations>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRefLocations>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetRefLocationsQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRefLocations>>> = ({ signal }) =>
        getRefLocations({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getRefLocations>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Get All Remarks
 */
export type RemarkCase = 'common' | 'report_no_seed' | 'material' | 'trial'

export const getAllRemarks = (remarkCase?: RemarkCase, options?: AxiosRequestConfig): Promise<AxiosResponse<RemarkModel[]>> => {
    const url = remarkCase ? `/api/v1/remarks/${remarkCase}` : `/api/v1/remarks`;
    return axios.get(url, options)
}

export const getGetAllRemarksQueryKey = (remarkCase?: RemarkCase) => [`/api/v1/remarks`, remarkCase]

export type GetAllRemarksQueryResult = NonNullable<Awaited<ReturnType<typeof getAllRemarks>>>
export type GetAllRemarksQueryError = AxiosError<unknown>

export const useGetAllRemarks = <
    TData = Awaited<ReturnType<typeof getAllRemarks>>,
    TError = AxiosError<unknown>,
>(remarkCase?: RemarkCase, options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getAllRemarks>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetAllRemarksQueryKey(remarkCase)

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllRemarks>>> = ({ signal }) =>
        getAllRemarks(remarkCase, { signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getAllRemarks>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}



/**
 * @summary Reports Full Export
 */
export const reportsFullExport = (options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
    return axios.post(`/api/v1/export`, undefined, options)
}

export type ReportsFullExportMutationResult = NonNullable<Awaited<ReturnType<typeof reportsFullExport>>>

export type ReportsFullExportMutationError = AxiosError<unknown>

export const useReportsFullExport = <TError = AxiosError<unknown>, TVariables = void, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof reportsFullExport>>, TError, TVariables, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof reportsFullExport>>, TVariables> = () => {
        return reportsFullExport(axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof reportsFullExport>>, TError, TVariables, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Get All Seed Processing Lots
 */
export const getAllSeedProcessingLots = (
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SeedProcessingLotModel[]>> => {
    return axios.get(`/api/v1/seed-processing-lot`, options)
}

export const getGetAllSeedProcessingLotsQueryKey = () => [`/api/v1/seed-processing-lot`]

export type GetAllSeedProcessingLotsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllSeedProcessingLots>>>
export type GetAllSeedProcessingLotsQueryError = AxiosError<unknown>

export const useGetAllSeedProcessingLots = <
    TData = Awaited<ReturnType<typeof getAllSeedProcessingLots>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getAllSeedProcessingLots>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetAllSeedProcessingLotsQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllSeedProcessingLots>>> = ({ signal }) =>
        getAllSeedProcessingLots({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getAllSeedProcessingLots>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Get All Seed Processing Lots With Material Counts
 */
export const getAllSeedProcessingLotsWithMaterialCounts = (
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SeedProcessingLotMaterials[]>> => {
    return axios.get(`/api/v1/seed-processing-lot-material-counts`, options)
}

export const getGetAllSeedProcessingLotsWithMaterialCountsQueryKey = () => [
    `/api/v1/seed-processing-lot-material-counts`,
]

export type GetAllSeedProcessingLotsWithMaterialCountsQueryResult = NonNullable<
    Awaited<ReturnType<typeof getAllSeedProcessingLotsWithMaterialCounts>>
>
export type GetAllSeedProcessingLotsWithMaterialCountsQueryError = AxiosError<unknown>

export const useGetAllSeedProcessingLotsWithMaterialCounts = <
    TData = Awaited<ReturnType<typeof getAllSeedProcessingLotsWithMaterialCounts>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getAllSeedProcessingLotsWithMaterialCounts>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetAllSeedProcessingLotsWithMaterialCountsQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllSeedProcessingLotsWithMaterialCounts>>> = ({
        signal,
    }) => getAllSeedProcessingLotsWithMaterialCounts({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getAllSeedProcessingLotsWithMaterialCounts>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Update Seed Processing Lot Name
 */
export const updateSeedProcessingLotName = (
    splotId: number,
    newName: string,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SeedProcessingLotModel>> => {
    return axios.put(`/api/v1/seed-processing-lot${splotId}/update-name/${newName}`, undefined, options)
}

export type UpdateSeedProcessingLotNameMutationResult = NonNullable<
    Awaited<ReturnType<typeof updateSeedProcessingLotName>>
>

export type UpdateSeedProcessingLotNameMutationError = AxiosError<HTTPValidationError>

export const useUpdateSeedProcessingLotName = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof updateSeedProcessingLotName>>,
        TError,
        { splotId: number; newName: string },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof updateSeedProcessingLotName>>,
        { splotId: number; newName: string }
    > = (props) => {
        const { splotId, newName } = props ?? {}

        return updateSeedProcessingLotName(splotId, newName, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof updateSeedProcessingLotName>>,
        TError,
        { splotId: number; newName: string },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * @summary Get Seed Processing Lot By Name
 */
export const getSeedProcessingLotByName = (
    splotName: string,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SeedProcessingLotWithMaterialCount>> => {
    return axios.get(`/api/v1/seed-processing-lot/${splotName}/by-name`, options)
}

export const getGetSeedProcessingLotByNameQueryKey = (splotName: string) => [
    `/api/v1/seed-processing-lot/${splotName}/by-name`,
]

export type GetSeedProcessingLotByNameQueryResult = NonNullable<Awaited<ReturnType<typeof getSeedProcessingLotByName>>>
export type GetSeedProcessingLotByNameQueryError = AxiosError<HTTPValidationError>

export const useGetSeedProcessingLotByName = <
    TData = Awaited<ReturnType<typeof getSeedProcessingLotByName>>,
    TError = AxiosError<HTTPValidationError>,
>(
    splotName: string,
    options?: {
        query?: UseQueryOptions<Awaited<ReturnType<typeof getSeedProcessingLotByName>>, TError, TData>
        axios?: AxiosRequestConfig
    }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetSeedProcessingLotByNameQueryKey(splotName)

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSeedProcessingLotByName>>> = ({ signal }) =>
        getSeedProcessingLotByName(splotName, { signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getSeedProcessingLotByName>>, TError, TData>(queryKey, queryFn, {
        enabled: !!splotName,
        ...queryOptions,
    }) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Delete Seed Processing Lot By Name
 */
export const deleteSeedProcessingLotByName = (
    splotName: string,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(`/api/v1/seed-processing-lot/${splotName}/by-name`, options)
}

export type DeleteSeedProcessingLotByNameMutationResult = NonNullable<
    Awaited<ReturnType<typeof deleteSeedProcessingLotByName>>
>

export type DeleteSeedProcessingLotByNameMutationError = AxiosError<HTTPValidationError>

export const useDeleteSeedProcessingLotByName = <
    TError = AxiosError<HTTPValidationError>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteSeedProcessingLotByName>>,
        TError,
        { splotName: string },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof deleteSeedProcessingLotByName>>,
        { splotName: string }
    > = (props) => {
        const { splotName } = props ?? {}

        return deleteSeedProcessingLotByName(splotName, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof deleteSeedProcessingLotByName>>,
        TError,
        { splotName: string },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * @summary Delete Seed Processing Lot By Id
 */
export const deleteSeedProcessingLotById = (
    splotId: number,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(`/api/v1/seed-processing-lot/${splotId}/by-id`, options)
}

export type DeleteSeedProcessingLotByIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof deleteSeedProcessingLotById>>
>

export type DeleteSeedProcessingLotByIdMutationError = AxiosError<HTTPValidationError>

export const useDeleteSeedProcessingLotById = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteSeedProcessingLotById>>,
        TError,
        { splotId: number },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteSeedProcessingLotById>>, { splotId: number }> = (
        props
    ) => {
        const { splotId } = props ?? {}

        return deleteSeedProcessingLotById(splotId, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof deleteSeedProcessingLotById>>, TError, { splotId: number }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Search Selection List Row
 */
export const searchSelectionListRow = (
    params?: SearchSelectionListRowParams,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SelectionListRowModel[]>> => {
    return axios.get(`/api/v1/selection-list-row/search`, {
        ...options,
        params: { ...params, ...options?.params },
    })
}

export const getSearchSelectionListRowQueryKey = (params?: SearchSelectionListRowParams) => [
    `/api/v1/selection-list-row/search`,
    ...(params ? [params] : []),
]

export type SearchSelectionListRowQueryResult = NonNullable<Awaited<ReturnType<typeof searchSelectionListRow>>>
export type SearchSelectionListRowQueryError = AxiosError<HTTPValidationError>

export const useSearchSelectionListRow = <
    TData = Awaited<ReturnType<typeof searchSelectionListRow>>,
    TError = AxiosError<HTTPValidationError>,
>(
    params?: SearchSelectionListRowParams,
    options?: {
        query?: UseQueryOptions<Awaited<ReturnType<typeof searchSelectionListRow>>, TError, TData>
        axios?: AxiosRequestConfig
    }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getSearchSelectionListRowQueryKey(params)

    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchSelectionListRow>>> = ({ signal }) =>
        searchSelectionListRow(params, { signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof searchSelectionListRow>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Get All Status
 */
export const getAllStatus = (options?: AxiosRequestConfig): Promise<AxiosResponse<StatusModel[]>> => {
    return axios.get(`/api/v1/statuses`, options)
}

export const getGetAllStatusQueryKey = () => [`/api/common/v1/breeder-statuses`]

export type GetAllStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getAllStatus>>>
export type GetAllStatusQueryError = AxiosError<unknown>

export const useGetAllStatus = <
    TData = Awaited<ReturnType<typeof getAllStatus>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getAllStatus>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetAllStatusQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllStatus>>> = ({ signal }) =>
        getAllStatus({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getAllStatus>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Create Status
 */
export const createStatus = (
    statusKeyValue: StatusKeyValue,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<StatusModel[]>> => {
    return axios.post(`/api/common/v1/status`, statusKeyValue, options)
}

export type CreateStatusMutationResult = NonNullable<Awaited<ReturnType<typeof createStatus>>>
export type CreateStatusMutationBody = StatusKeyValue
export type CreateStatusMutationError = AxiosError<HTTPValidationError>

export const useCreateStatus = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof createStatus>>, TError, { data: StatusKeyValue }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof createStatus>>, { data: StatusKeyValue }> = (
        props
    ) => {
        const { data } = props ?? {}

        return createStatus(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof createStatus>>, TError, { data: StatusKeyValue }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Search Trials
 */
export const searchTrials = (
    trialSearchParams: TrialSearchParams,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<TrialMaterialModel[]>> => {
    return axios.post(`/api/v1/trial/search`, trialSearchParams, options)
}

export type SearchTrialsMutationResult = NonNullable<Awaited<ReturnType<typeof searchTrials>>>
export type SearchTrialsMutationBody = TrialSearchParams
export type SearchTrialsMutationError = AxiosError<HTTPValidationError>

export const useSearchTrials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof searchTrials>>,
        TError,
        { data: TrialSearchParams },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchTrials>>, { data: TrialSearchParams }> = (
        props
    ) => {
        const { data } = props ?? {}

        return searchTrials(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof searchTrials>>, TError, { data: TrialSearchParams }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Recalculate Trial Requirements
 */
export const recalculateTrialRequirements = (
    trialBid: string,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.post(`/api/v1/trial/trial-materials/${trialBid}/recalculate-requirements`, undefined, options)
}

export type RecalculateTrialRequirementsMutationResult = NonNullable<
    Awaited<ReturnType<typeof recalculateTrialRequirements>>
>

export type RecalculateTrialRequirementsMutationError = AxiosError<HTTPValidationError>

export const useRecalculateTrialRequirements = <
    TError = AxiosError<HTTPValidationError>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof recalculateTrialRequirements>>,
        TError,
        { trialBid: string },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof recalculateTrialRequirements>>,
        { trialBid: string }
    > = (props) => {
        const { trialBid } = props ?? {}

        return recalculateTrialRequirements(trialBid, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof recalculateTrialRequirements>>,
        TError,
        { trialBid: string },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * @summary Recalculate Trial Material Requirements
 */
export const recalculateTrialMaterialRequirements = (
    trialBid: string,
    material: string,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.post(
        `/api/v1/trial/trial-materials/${trialBid}/recalculate-requirements/${material}`,
        undefined,
        options
    )
}

export type RecalculateTrialMaterialRequirementsMutationResult = NonNullable<
    Awaited<ReturnType<typeof recalculateTrialMaterialRequirements>>
>

export type RecalculateTrialMaterialRequirementsMutationError = AxiosError<HTTPValidationError>

export const useRecalculateTrialMaterialRequirements = <
    TError = AxiosError<HTTPValidationError>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof recalculateTrialMaterialRequirements>>,
        TError,
        { trialBid: string; material: string },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof recalculateTrialMaterialRequirements>>,
        { trialBid: string; material: string }
    > = (props) => {
        const { trialBid, material } = props ?? {}

        return recalculateTrialMaterialRequirements(trialBid, material, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof recalculateTrialMaterialRequirements>>,
        TError,
        { trialBid: string; material: string },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * @summary Get All Treatments
 */
// export const getAllTreatments = (options?: AxiosRequestConfig): Promise<AxiosResponse<TreatmentModel1[]>> => {
//     return axios.get(`/api/v1/treatment`, options)
// }

// export const getGetAllTreatmentsQueryKey = () => [`/api/v1/treatment`]

// export type GetAllTreatmentsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllTreatments>>>
// export type GetAllTreatmentsQueryError = AxiosError<unknown>

// export const useGetAllTreatments = <
//     TData = Awaited<ReturnType<typeof getAllTreatments>>,
//     TError = AxiosError<unknown>,
// >(options?: {
//     query?: UseQueryOptions<Awaited<ReturnType<typeof getAllTreatments>>, TError, TData>
//     axios?: AxiosRequestConfig
// }): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
//     const { query: queryOptions, axios: axiosOptions } = options ?? {}

//     const queryKey = queryOptions?.queryKey ?? getGetAllTreatmentsQueryKey()

//     const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllTreatments>>> = ({ signal }) =>
//         getAllTreatments({ signal, ...axiosOptions })

//     const query = useQuery<Awaited<ReturnType<typeof getAllTreatments>>, TError, TData>(
//         queryKey,
//         queryFn,
//         queryOptions
//     ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

//     query.queryKey = queryKey

//     return query
// }

/**
 * @summary Create Treatment
 */
export const createTreatment = (
    treatmentModel1: TreatmentModel1,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/treatment`, treatmentModel1, options)
}

export type CreateTreatmentMutationResult = NonNullable<Awaited<ReturnType<typeof createTreatment>>>
export type CreateTreatmentMutationBody = TreatmentModel1
export type CreateTreatmentMutationError = AxiosError<HTTPValidationError>

export const useCreateTreatment = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof createTreatment>>,
        TError,
        { data: TreatmentModel1 },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTreatment>>, { data: TreatmentModel1 }> = (
        props
    ) => {
        const { data } = props ?? {}

        return createTreatment(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof createTreatment>>, TError, { data: TreatmentModel1 }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * Update all sequences to the lowest number that won't cause a conflict. This is
generally only needed when rows have been synced from one database to another.
<br/>
The following sequences are updated.
 * @summary Update Sequences
 */
export const updateSequences = (options?: AxiosRequestConfig): Promise<AxiosResponse<unknown>> => {
    return axios.get(`/api/v1/update-sequences`, options)
}

export const getUpdateSequencesQueryKey = () => [`/api/v1/update-sequences`]

export type UpdateSequencesQueryResult = NonNullable<Awaited<ReturnType<typeof updateSequences>>>
export type UpdateSequencesQueryError = AxiosError<unknown>

export const useUpdateSequences = <
    TData = Awaited<ReturnType<typeof updateSequences>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof updateSequences>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getUpdateSequencesQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof updateSequences>>> = ({ signal }) =>
        updateSequences({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof updateSequences>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Get All Users
 */
export const getAllUsers = (options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel[]>> => {
    return axios.get(`/api/common/v1/users`, options)
}
export const getWorkflowDataApi = () => {
    return axios.get(`/api/v1/workflow-protocols`, {})
}

export const getAllTemplatesApi = () => {
    return axios.get(`/api/v1/label-templates`, {})
}

export const getAllTemplates = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getAllTemplatesApi>>, TError, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getAllTemplatesApi>>> = () => {
        return getAllTemplatesApi()
    }

    return useMutation<Awaited<ReturnType<typeof getAllTemplatesApi>>, TError, TContext>(
        mutationFn,
        mutationOptions
    )
}

export const getWorkflowData = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getWorkflowDataApi>>, TError, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getWorkflowDataApi>>> = () => {

        return getWorkflowDataApi()
    }

    return useMutation<Awaited<ReturnType<typeof getWorkflowDataApi>>, TError, TContext>(
        mutationFn,
        mutationOptions
    )
}
export const getGetAllUsersQueryKey = () => [`/api/commonv1/users`]

export type GetAllUsersQueryResult = NonNullable<Awaited<ReturnType<typeof getAllUsers>>>
export type GetAllUsersQueryError = AxiosError<unknown>

export const useGetAllUsers = <
    TData = Awaited<ReturnType<typeof getAllUsers>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetAllUsersQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllUsers>>> = ({ signal }) =>
        getAllUsers({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getAllUsers>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Get Logged In User
 */
export const getLoggedInUser = (emailId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel>> => {
    return axios.get(`/api/common/v1/user?user_email=${emailId}`, options)
}

export const getGetLoggedInUserQueryKey = () => [`/api/v1/get-logged-in-user/`]

export type GetLoggedInUserQueryResult = NonNullable<Awaited<ReturnType<typeof getLoggedInUser>>>
export type GetLoggedInUserQueryError = AxiosError<unknown>

export const useGetLoggedInUser = <
    TData = Awaited<ReturnType<typeof getLoggedInUser>>,
    TError = AxiosError<unknown>,
>(emailId: string, options?: {

    query?: UseQueryOptions<Awaited<ReturnType<typeof getLoggedInUser>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetLoggedInUserQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLoggedInUser>>> = ({ signal }) =>
        getLoggedInUser(emailId, { signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getLoggedInUser>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Update User
 */
export const updateUser = (userModel: UserModel, options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel>> => {
    return axios.post(`/api/common/v1/update-user`, userModel, options)
}

export type UpdateUserMutationResult = NonNullable<Awaited<ReturnType<typeof updateUser>>>
export type UpdateUserMutationBody = UserModel
export type UpdateUserMutationError = AxiosError<HTTPValidationError>

export const useUpdateUser = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof updateUser>>, TError, { data: UserModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateUser>>, { data: UserModel }> = (props) => {
        const { data } = props ?? {}

        return updateUser(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof updateUser>>, TError, { data: UserModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Add User
 */
export const addUser = (userModel: UserModel, options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel>> => {
    return axios.post(`/api/common/v1/add-user`, userModel, options)
}

export type AddUserMutationResult = NonNullable<Awaited<ReturnType<typeof addUser>>>
export type AddUserMutationBody = UserModel
export type AddUserMutationError = AxiosError<HTTPValidationError>

export const useAddUser = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof addUser>>, TError, { data: UserModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof addUser>>, { data: UserModel }> = (props) => {
        const { data } = props ?? {}

        return addUser(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof addUser>>, TError, { data: UserModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Delete User
 */
export const deleteUser = (userModel: UserModel, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
    return axios.delete(`/api/common/v1/delete?user_id=${userModel.user_id}&deleted_by=${userModel.deleted_by}`, { data: userModel, ...options })
}

export type DeleteUserMutationResult = NonNullable<Awaited<ReturnType<typeof deleteUser>>>
export type DeleteUserMutationBody = UserModel
export type DeleteUserMutationError = AxiosError<HTTPValidationError>

export const useDeleteUser = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof deleteUser>>, TError, { data: UserModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteUser>>, { data: UserModel }> = (props) => {
        const { data } = props ?? {}

        return deleteUser(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof deleteUser>>, TError, { data: UserModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/* get all modules  */

export const useGetModules = <
    TData = Awaited<ReturnType<typeof getRefModules>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRefModules>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetRefModulesQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRefModules>>> = ({ signal }) =>
        getRefModules({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getRefModules>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}
export const getRefModules = (options?: AxiosRequestConfig): Promise<AxiosResponse<ModuleDataModel[]>> => {
    return axios.get(`/api/common/v1/modules`, options)
}

export const getGetRefModulesQueryKey = () => [`/api/common/v1/modules`]


/* get all roles  */

export const useGetRoles = <
    TData = Awaited<ReturnType<typeof getRefRoles>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRefRoles>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetRefRolesQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRefRoles>>> = ({ signal }) =>
        getRefRoles({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getRefRoles>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}
export const getRefRoles = (options?: AxiosRequestConfig): Promise<AxiosResponse<ModuleRolesDataModel[]>> => {
    return axios.get(`/api/common/v1/modules-with-roles`, options)
}

export const getGetRefRolesQueryKey = () => [`/api/common/v1/modules-with-roles`]


/**
 * @summary Search  User
 */
export const searchUser = (searchUserModel: SearchUserModel, options?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.post(`/api/common/v1/search-users`, searchUserModel, options)
}

export type SearchUserMutationResult = NonNullable<Awaited<ReturnType<typeof searchUser>>>
export type SearchUserMutationBody = SearchUserModel
export type SearchUserMutationError = AxiosError<HTTPValidationError>

export const searchUserCall = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof searchUser>>, TError, { data: SearchUserModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchUser>>, { data: SearchUserModel }> = (props) => {
        const { data } = props ?? {}

        return searchUser(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof searchUser>>, TError, { data: SearchUserModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary get All User
 */
export const getAllUsersListApi = () => {
    return axios.get(`/api/common/v1/users`, {})
}

export type GetAllUsersListMutationResult = NonNullable<Awaited<ReturnType<typeof getAllUsersListApi>>>
export type GetAllUsersListMutationBody = SearchUserModel
export type GetAllUsersListMutationError = AxiosError<HTTPValidationError>

export const getAllUsersList = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getAllUsersListApi>>, TError, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getAllUsersListApi>>> = () => {

        return getAllUsersListApi()
    }

    return useMutation<Awaited<ReturnType<typeof getAllUsersListApi>>, TError, TContext>(
        mutationFn,
        mutationOptions
    )
}

/* View SP Lots  */
export const getSPLots = (siteCode: string, options?: AxiosRequestConfig): Promise<AxiosResponse<SPLotModel>> => {
    return axios.get(`/api/v1/splots/${encodeURIComponent(siteCode)}`, options)
}

export const usegetSPLots = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getSPLots>>, TError, { siteCode: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getSPLots>>, { siteCode: string }> = (props) => {
        return getSPLots(props.siteCode, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getSPLots>>, TError, { siteCode: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}


/* drill_down_splot_by_id  */

export const getDrillDownSPLotById = (splot_id: string, options?: AxiosRequestConfig, sort_by?: string | null, sort_order?: string | null): Promise<AxiosResponse<SPLotModel>> => {
    let appendReq = sort_by ? `&sort_by=${sort_by}${sort_order ? `&sort_order=${sort_order}` : ''}` : '';
    appendReq = appendReq ? `?${appendReq.slice(1)}` : '';
    return axios.get(`/api/v1/splot-materials/${encodeURIComponent(splot_id)}${appendReq}`, options)
}

export const useGetDrillDownSPLotById = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getDrillDownSPLotById>>, TError, { splot_id: string, sort_by?: string | null, sort_order?: string | null }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getDrillDownSPLotById>>, { splot_id: string, sort_by?: string | null, sort_order?: string | null }> = (props) => {
        return getDrillDownSPLotById(props.splot_id, axiosOptions, props.sort_by, props.sort_order)
    }

    return useMutation<Awaited<ReturnType<typeof getDrillDownSPLotById>>, TError, { splot_id: string, sort_by?: string | null, sort_order?: string | null }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary  Create SPLot
 */
export const createSPLot = (createSPLotModel: CreateSPLotModel, options?: AxiosRequestConfig): Promise<AxiosResponse<CreateSPLotModel>> => {
    return axios.post(`/api/v1/splots`, createSPLotModel, options)
}

export type CreateSPLotMutationResult = NonNullable<Awaited<ReturnType<typeof createSPLot>>>
export type CreateSPLotMutationBody = CreateSPLotModel
export type CreateSPLotMutationError = AxiosError<HTTPValidationError>

export const useCreateSPLot = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof createSPLot>>, TError, { data: CreateSPLotModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSPLot>>, { data: CreateSPLotModel }> = (props) => {
        const { data } = props ?? {}

        return createSPLot(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof createSPLot>>, TError, { data: CreateSPLotModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary  Add Material
 */
export const addMaterial = (addMaterialModel: AddMaterialModel, options?: AxiosRequestConfig): Promise<AxiosResponse<CreateSPLotModel>> => {
    return axios.post(`/api/v1/splot-materials`, addMaterialModel, options)
}
export const useAddMaterial = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof addMaterial>>, TError, { data: AddMaterialModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof addMaterial>>, { data: AddMaterialModel }> = (props) => {
        const { data } = props ?? {}

        return addMaterial(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof addMaterial>>, TError, { data: AddMaterialModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Search  SPLot
 */
export const createFilterSPLot = (createFilterSPLotModel: CreateFilterSPLotModel, options?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.post(`/api/v1/create_splot_filter`, createFilterSPLotModel, options)
}


export const useCreateFilterSPLot = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof createFilterSPLot>>, TError, { data: CreateFilterSPLotModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof createFilterSPLot>>, { data: CreateFilterSPLotModel }> = (props) => {
        const { data } = props ?? {}

        return createFilterSPLot(data, axiosOptions)
    }
    return useMutation<Awaited<ReturnType<typeof createFilterSPLot>>, TError, { data: CreateFilterSPLotModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/* View Trials  */


export const getTrials = (model: any, siteCode?: string, options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel>> => {
    return axios.get(`/api/v1/postseason-trials/${encodeURIComponent(siteCode ?? '')}?page=${model.page}&page_size=${model.pageSize}`, options)
}

export const useGetTrials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getTrials>>, TError, { model: any; siteCode?: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getTrials>>, { model: any; siteCode?: string }> = ({ model, siteCode }) => {
        return getTrials(model, siteCode, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getTrials>>, TError, { model: any; siteCode?: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}

// Paginated Trials by Status
export interface GetPaginatedTrialsByStatusBody {
    status_id: number[];
    site_code: string;
    page: number;
    page_size: number;
}

export const getPaginatedTrialsByStatus = (data: GetPaginatedTrialsByStatusBody, options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel>> => {
    return axios.post(`/api/v1/trials-by-status`, data, options)
}

export const useGetPaginatedTrialsByStatus = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getPaginatedTrialsByStatus>>, TError, { data: GetPaginatedTrialsByStatusBody }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getPaginatedTrialsByStatus>>, { data: GetPaginatedTrialsByStatusBody }> = ({ data }) => {
        return getPaginatedTrialsByStatus(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getPaginatedTrialsByStatus>>, TError, { data: GetPaginatedTrialsByStatusBody }, TContext>(
        mutationFn,
        mutationOptions
    )
}

// High Priority Trials
export const getHighPriorityTrials = (model: any, siteCode?: string, options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel>> => {
    return axios.get(`/api/v1/high-priority-trials/${encodeURIComponent(siteCode ?? '')}?page=${model.page}&pageSize=${model.pageSize}`, options)
}

export const useGetHighPriorityTrials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getHighPriorityTrials>>, TError, { model: any; siteCode?: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getHighPriorityTrials>>, { model: any; siteCode?: string }> = ({ model, siteCode }) => {
        return getHighPriorityTrials(model, siteCode, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getHighPriorityTrials>>, TError, { model: any; siteCode?: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Search Trials by Filters
 */
export const searchTrialsByFilters = (
    trailSearchRequestBody: TrailSearchRequestBody,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/postseason-filtered-trials`, trailSearchRequestBody, options)
}

export type SearchTrialsByFiltersMutationResult = NonNullable<Awaited<ReturnType<typeof searchTrialsByFilters>>>
export type SearchTrialsByFiltersMutationBody = TrailSearchRequestBody
export type SearchTrialsByFiltersMutationError = AxiosError<HTTPValidationError>

export const useSearchTrialsByFilters = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof searchTrialsByFilters>>,
        TError,
        { requestBody: TrailSearchRequestBody },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchTrialsByFilters>>, { requestBody: TrailSearchRequestBody }> = (
        props
    ) => {
        const { requestBody } = props ?? {}

        return searchTrialsByFilters(requestBody, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof searchTrialsByFilters>>, TError, { requestBody: TrailSearchRequestBody }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Search  SPLot
 */
export const searchFilterSPLot = (filterSPLotModel: FilterSPLotModel, options?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.post(`/api/v1/splots-search`, filterSPLotModel, options)
}

export const useSearchFilterSPLot = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof searchFilterSPLot>>, TError, { data: FilterSPLotModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchFilterSPLot>>, { data: FilterSPLotModel }> = (props) => {
        const { data } = props ?? {}

        return searchFilterSPLot(data, axiosOptions)
    }
    return useMutation<Awaited<ReturnType<typeof searchFilterSPLot>>, TError, { data: FilterSPLotModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Get All Status
 */
export const getAllPriority = (options?: AxiosRequestConfig): Promise<AxiosResponse<StatusModel[]>> => {
    return axios.get(`/api/v1/processing-priorities`, options)
}
export const getAllPriorityQueryKey = () => [`/api/v1/processing-priorities`]

export const useGetAllPriority = <
    TData = Awaited<ReturnType<typeof getAllPriority>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getAllPriority>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetAllStatusQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllPriority>>> = ({ signal }) =>
        getAllPriority({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getAllPriority>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

export const fetchMaterialDetails = (
    data: { container_alt_ids: string[] },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/material-details`, data, options)
}

export type FetchMaterialDetailsMutationResult = NonNullable<Awaited<ReturnType<typeof fetchMaterialDetails>>>
export type FetchMaterialDetailsMutationBody = { container_ids: string[] }
export type FetchMaterialDetailsMutationError = AxiosError<HTTPValidationError>

export const useFetchMaterialDetails = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof fetchMaterialDetails>>,
        TError,
        { data: { container_alt_ids: string[] } },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof fetchMaterialDetails>>,
        { data: { container_alt_ids: string[] } }
    > = (props) => {
        const { data } = props ?? {}

        return fetchMaterialDetails(data, axiosOptions)
    }

    return useMutation<
        Awaited<ReturnType<typeof fetchMaterialDetails>>,
        TError,
        { data: { container_alt_ids: string[] } },
        TContext
    >(mutationFn, mutationOptions)
}

/**
 * @summary Register Material
 */
export const registerMaterial = (
    materialBody: MaterialBody,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/mint/register-material`, materialBody, options)
}


export type RegisterMaterialMutationResult = NonNullable<Awaited<ReturnType<typeof registerMaterial>>>
export type RegisterMaterialMutationBody = MaterialBody
export type RegisterMaterialMutationError = AxiosError<HTTPValidationError>


export const useRegisterMaterial = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof registerMaterial>>,
        TError,
        { data: MaterialBody },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof registerMaterial>>, { data: MaterialBody }> = (
        props
    ) => {
        const { data } = props ?? {}

        return registerMaterial(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof registerMaterial>>, TError, { data: MaterialBody }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary  Update Material
 */
export const editMaterial = (editMaterialModel: AddMaterialModel, options?: AxiosRequestConfig): Promise<AxiosResponse<CreateSPLotModel>> => {
    return axios.put(`/api/v1/splot-materials`, editMaterialModel, options)
}
export const useEditMaterial = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof editMaterial>>, TError, { data: AddMaterialModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof editMaterial>>, { data: AddMaterialModel }> = (props) => {
        const { data } = props ?? {}

        return editMaterial(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof editMaterial>>, TError, { data: AddMaterialModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Search SPLot by ID
 */
export const searchBoxSPLot = (search_id: string, is_archive: boolean = false, options?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.get(`/api/v1/splots/${encodeURIComponent(search_id)}?is_archive=${is_archive}`, options)
}

export const useSearchBoxSPLot = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof searchBoxSPLot>>, TError, { splot_id: string; is_archive?: boolean }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchBoxSPLot>>, { splot_id: string; is_archive?: boolean }> = (props) => {
        return searchBoxSPLot(props.splot_id, props.is_archive ?? false, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof searchBoxSPLot>>, TError, { splot_id: string; is_archive?: boolean }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Search Trial by ID
 */
export const searchBoxTrial = (search_id: string, page: number = 1, page_size: number = 50, is_archive: boolean = false, options?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.get(`/api/v1/postseason-trial-search/${encodeURIComponent(search_id)}?page=${page}&page_size=${page_size}&is_archive=${is_archive}`, options)
}

export const useSearchBoxTrial = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof searchBoxTrial>>, TError, { trial_id: string; page?: number; page_size?: number; is_archive?: boolean }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchBoxTrial>>, { trial_id: string; page?: number; page_size?: number; is_archive?: boolean }> = (props) => {
        return searchBoxTrial(props.trial_id, props.page ?? 1, props.page_size ?? 50, props.is_archive ?? false, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof searchBoxTrial>>, TError, { trial_id: string; page?: number; page_size?: number; is_archive?: boolean }, TContext>(
        mutationFn,
        mutationOptions
    )
}




export const getFilters = (user_id: number, is_archive: boolean, url: string, options?: AxiosRequestConfig, pathParams?: Array<'user_id' | 'is_archive'>): Promise<AxiosResponse<GetFiltersModel>> => {
    if (pathParams?.length) {
        const values = { user_id, is_archive };
        const segments = pathParams.map((key) => values[key]).join('/');
        return axios.get(`${url}/${segments}`, options)
    }
    return axios.get(`${url}?user_id=${user_id}&is_archive=${is_archive}`, options)
}

export const getFiltersQueryKey = () => [`/api/v1/splot_filters/`]

export const useGetFilters = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getFilters>>, TError, { user_id: number, url: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}
    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getFilters>>, { user_id: number, is_archive: boolean, url: string, pathParams?: Array<'user_id' | 'is_archive'> }> = (props) => {
        return getFilters(props.user_id, props.is_archive, props.url, axiosOptions, props.pathParams)
    }
    return useMutation<Awaited<ReturnType<typeof getFilters>>, TError, { user_id: number, is_archive: boolean, url: string, pathParams?: Array<'user_id' | 'is_archive'> }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Search  searchFilter
 */
export const searchFilterData = (filterSPLotModel: Record<string, any>, url: string, options?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.post(`${url}`, filterSPLotModel, options)
}

export const useSearchFilterData = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof searchFilterData>>, TError, { data: Record<string, any>, url: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchFilterData>>, { data: Record<string, any>, url: string }> = (props) => {
        const { data, url } = props ?? {}

        return searchFilterData(data, url, axiosOptions)
    }
    return useMutation<Awaited<ReturnType<typeof searchFilterData>>, TError, { data: Record<string, any>, url: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}

export const createFilter = (createFilterSPLotModel: Record<string, any>, url: string, options?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.post(`${url}`, createFilterSPLotModel, options)
}

export const useCreateFilter = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof createFilter>>, TError, { data: Record<string, any>, url: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof createFilter>>, { data: Record<string, any>, url: string }> = (props) => {
        const { data, url } = props ?? {}

        return createFilter(data, url, axiosOptions)
    }
    return useMutation<Awaited<ReturnType<typeof createFilter>>, TError, { data: Record<string, any>, url: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Delete Trial Filter
 */
export interface DeleteFilterModel {
    user_id: number;
    filter_name: string;
    filter_id: number;
    // Which field is embedded in the URL path; defaults to filter_id.
    pathKey?: 'filter_id' | 'filter_name';
    // 'path' (filter_id/name + query in URL, no body) or 'body' (original contract: DELETE url with body). Defaults to 'path'.
    mode?: 'path' | 'body';
}

export const deleteFilter = (deleteFilterModel: DeleteFilterModel, url: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> => {
    const { filter_id, user_id, filter_name, pathKey = 'filter_id', mode = 'path' } = deleteFilterModel;
    if (mode === 'body') {
        return axios.delete(`${url}`, { data: { user_id, filter_name, filter_id }, ...options })
    }
    const pathValue = pathKey === 'filter_name' ? encodeURIComponent(filter_name) : filter_id;
    return axios.delete(`${url}/${pathValue}?user_id=${user_id}&filter_name=${encodeURIComponent(filter_name)}`, options)
}

export type DeleteFilterMutationResult = NonNullable<Awaited<ReturnType<typeof deleteFilter>>>
export type DeleteFilterMutationBody = DeleteFilterModel
export type DeleteFilterMutationError = AxiosError<HTTPValidationError>

export const useDeleteFilter = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof deleteFilter>>, TError, { data: DeleteFilterModel, url: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteFilter>>, { data: DeleteFilterModel, url: string }> = (props) => {
        const { data, url } = props ?? {}

        return deleteFilter(data, url, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof deleteFilter>>, TError, { data: DeleteFilterModel, url: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary get material by trial id
 */
export const getMaterialsByTrial = (data: any, options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel>> => {
    const page = data.page != null ? data.page + 1 : 1;
    const pageSize = data.pageSize ?? 100;
    let appendReq = data.sort_by ? `&sort_by=${data.sort_by}` : '';
    appendReq += data.sort_order ? `&sort_order=${data.sort_order}` : '';
    return axios.get(`/api/v1/trial-materials/${data.trial_bid}?page=${page}&pageSize=${pageSize}${appendReq}`, options)
}

export const useGetMaterialByTrial = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(trialBid: any, options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getMaterialsByTrial>>, TError, unknown, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getMaterialsByTrial>>, unknown> = () => {
        return getMaterialsByTrial(trialBid, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getMaterialsByTrial>>, TError, unknown, TContext>(
        mutationFn,
        mutationOptions
    )
}


/* View Archived SP Lots  */
export const getArchivedSPLots = (siteCode: string, options?: AxiosRequestConfig): Promise<AxiosResponse<SPLotModel>> => {
    return axios.get(`/api/v1/archived-splots/${encodeURIComponent(siteCode)}`, options)
}

export const useGetArchivedSPLots = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getArchivedSPLots>>, TError, { siteCode: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getArchivedSPLots>>, { siteCode: string }> = (props) => {
        return getArchivedSPLots(props.siteCode, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getArchivedSPLots>>, TError, { siteCode: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}

// Archived Trials

export const getArchivedTrials = (model: any, siteCode?: string, options?: AxiosRequestConfig): Promise<AxiosResponse<UserModel>> => {
    return axios.get(`/api/v1/postseason-archived-trials/${encodeURIComponent(siteCode ?? '')}?page=${model.page}&page_size=${model.pageSize}`, options)
}

export const useGetArchivedTrials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getArchivedTrials>>, TError, { model: any; siteCode?: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getArchivedTrials>>, { model: any; siteCode?: string }> = ({ model, siteCode }) => {
        return getArchivedTrials(model, siteCode, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getArchivedTrials>>, TError, { model: any; siteCode?: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary  Update Material
 */
export const updateSplot = (updateSpLot: UpdateSpLotModel, options?: AxiosRequestConfig): Promise<AxiosResponse<CreateSPLotModel>> => {
    const { splot_id } = updateSpLot;
    return axios.patch(`/api/v1/splots/${encodeURIComponent(splot_id)}`, updateSpLot, options)
}
export const useUpdateSplot = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof updateSplot>>, TError, { data: UpdateSpLotModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSplot>>, { data: UpdateSpLotModel }> = (props) => {
        const { data } = props ?? {}

        return updateSplot(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof updateSplot>>, TError, { data: UpdateSpLotModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Edit Trial Materials
 */
export const editTrialMaterials = (materials: EditTrialMaterialsRequestBody, options?: AxiosRequestConfig): Promise<AxiosResponse<unknown>> => {
    return axios.post(`/api/v1/trial-materials-edit`, materials, options)
}

export const useEditTrialMaterials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof editTrialMaterials>>, TError, { data: EditTrialMaterialsRequestBody }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof editTrialMaterials>>, { data: EditTrialMaterialsRequestBody }> = (props) => {
        const { data } = props ?? {}

        return editTrialMaterials(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof editTrialMaterials>>, TError, { data: EditTrialMaterialsRequestBody }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary  Update Material
 */
export const createMaterialContainer = (createLabelConData: CreateLabelContainerModel, options?: AxiosRequestConfig): Promise<AxiosResponse<CreateSPLotModel>> => {
    return axios.post(`/api/v1/material-containers`, createLabelConData, options)
}
export const useCreateMaterialContainer = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof createMaterialContainer>>, TError, { data: CreateLabelContainerModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMaterialContainer>>, { data: CreateLabelContainerModel }> = (props) => {
        const { data } = props ?? {}

        return createMaterialContainer(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof createMaterialContainer>>, TError, { data: CreateLabelContainerModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}


/* get SPloT by id */
export const getSPLotbyId = (splotId: string, options?: AxiosRequestConfig): Promise<AxiosResponse<SPLotModel>> => {
    return axios.get(`/api/v1/splots/${encodeURIComponent(splotId)}`, options)
}

export const useGetSPLotbyId = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getSPLotbyId>>, TError, { splotId: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getSPLotbyId>>, { splotId: string }> = (props) => {
        return getSPLotbyId(props.splotId, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getSPLotbyId>>, TError, { splotId: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary  Validate SPLoT
 */
export const validateSPLot = (splotIds: validateSPLotModel, options?: AxiosRequestConfig): Promise<AxiosResponse<CreateSPLotModel>> => {
    return axios.post(`/api/v1/splots-validation`, splotIds, options)
}
export const useValidateSPLot = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof validateSPLot>>, TError, { data: validateSPLotModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof validateSPLot>>, { data: validateSPLotModel }> = (props) => {
        const { data } = props ?? {}

        return validateSPLot(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof validateSPLot>>, TError, { data: validateSPLotModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Check Matid Exists
 */
export type CheckMatidExistParams = { matid?: string | null; splot_id?: string | null; crop_code?: string | null; site_code?: string | null; confirm_threshold?: boolean }

export const checkMatidExist = (
    params?: CheckMatidExistParams,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
    const { matid, confirm_threshold = false, ...queryParams } = params ?? {};
    return axios.get(`/api/v1/matid-exists-check/${encodeURIComponent(matid ?? '')}`, {
        ...options,
        params: { ...queryParams, confirm_threshold, ...options?.params },
    })
}

export const getCheckMatidExistQueryKey = (params?: CheckMatidExistParams) => [
    `/api/v1/matid-exists-check`,
    ...(params ? [params] : []),
]

export type CheckMatidExistQueryResult = NonNullable<Awaited<ReturnType<typeof checkMatidExist>>>
export type CheckMatidExistQueryError = AxiosError<HTTPValidationError>

export const useCheckMatidExist = <
    TData = Awaited<ReturnType<typeof checkMatidExist>>,
    TError = AxiosError<HTTPValidationError>,
>(
    params?: CheckMatidExistParams,
    options?: {
        query?: UseQueryOptions<Awaited<ReturnType<typeof checkMatidExist>>, TError, TData>
        axios?: AxiosRequestConfig
    }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getCheckMatidExistQueryKey(params)

    const queryFn: QueryFunction<Awaited<ReturnType<typeof checkMatidExist>>> = ({ signal }) =>
        checkMatidExist(params, { signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof checkMatidExist>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

export const useCheckMatidExistMutation = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof checkMatidExist>>, TError, { data: CheckMatidExistParams }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof checkMatidExist>>, { data: CheckMatidExistParams }> = (props) => {
        const { data } = props ?? {}
        return checkMatidExist(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof checkMatidExist>>, TError, { data: CheckMatidExistParams }, TContext>(
        mutationFn,
        mutationOptions
    )
}


// /**
//  * @summary Get Not Registered Container Count
//  */
// export const getNotRegisteredCount = (req:any,options?: AxiosRequestConfig): Promise<AxiosResponse<{ count: number }>> => {
//     return axios.get(`/api/v1/s2s-mint/count/not-registered?site_code=${}`, options)
// }

// export const getNotRegisteredCountQueryKey = () => [`/api/v1/s2s-mint/count/not-registered`]

// export const useGetNotRegisteredCount = <
//     TData = Awaited<ReturnType<typeof getNotRegisteredCount>>,
//     TError = AxiosError<unknown>,
// >(options?: {
//     query?: UseQueryOptions<Awaited<ReturnType<typeof getNotRegisteredCount>>, TError, { reqObj:any }>
//     axios?: AxiosRequestConfig
// }): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
//     const { query: queryOptions, axios: axiosOptions } = options ?? {}

//     const queryKey = queryOptions?.queryKey ?? getNotRegisteredCountQueryKey()

//     const queryFn: QueryFunction<Awaited<ReturnType<typeof getNotRegisteredCount>>> = ({ signal }) =>
//         getNotRegisteredCount({ signal, ...axiosOptions })

//     const query = useQuery<Awaited<ReturnType<typeof getNotRegisteredCount>>, TError, { reqObj:any }>(
//         queryKey,
//         queryFn,
//         queryOptions
//     ) as UseQueryResult<{ reqObj:any }, TError> & { queryKey: QueryKey }

//     query.queryKey = queryKey

//     return query
// }


/**
 * @summary  useGetNotRegisteredCount
 */
export const getNotRegisteredCount = (data: any, options?: AxiosRequestConfig): Promise<AxiosResponse<any>> => {
    return axios.get(`/api/v1/mint/unregistered-material-count/${encodeURIComponent(data?.siteCode ?? '')}`, options)
}

export const getetNotRegisteredCountQueryKey = () => [`/api/v1/mint/unregistered-material-count/`]
export const useGetNotRegisteredCount = <
    TData = Awaited<ReturnType<typeof getNotRegisteredCount>>,
    TError = AxiosError<unknown>,
>(data: any, options?: {

    query?: UseQueryOptions<Awaited<ReturnType<typeof getNotRegisteredCount>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getetNotRegisteredCountQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNotRegisteredCount>>> = ({ signal }) =>
        getNotRegisteredCount(data, { signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getNotRegisteredCount>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Get All  trial Status
 */
export const getAllTrialStatus = (options?: AxiosRequestConfig): Promise<AxiosResponse<StatusModel[]>> => {
    return axios.get(`/api/v1/trial-statuses`, options)
}
export const getAllTrialStatusQueryKey = () => [`/api/v1/trial-statuses`]

export const useGetAllTrialStatus = <
    TData = Awaited<ReturnType<typeof getAllTrialStatus>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getAllTrialStatus>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getAllTrialStatusQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllTrialStatus>>> = ({ signal }) =>
        getAllTrialStatus({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getAllTrialStatus>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Get All Label Templates
 */
export const getLabelTemplates = (options?: AxiosRequestConfig): Promise<AxiosResponse<LabelTemplate[]>> => {
    return axios.get(`/api/v1/label-templates`, options)
}

export const getGetLabelTemplatesQueryKey = () => [`/api/v1/label-templates`]

export type GetLabelTemplatesQueryResult = NonNullable<Awaited<ReturnType<typeof getLabelTemplates>>>
export type GetLabelTemplatesQueryError = AxiosError<unknown>

export const useGetLabelTemplates = <
    TData = Awaited<ReturnType<typeof getLabelTemplates>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getLabelTemplates>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getGetLabelTemplatesQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLabelTemplates>>> = ({ signal }) =>
        getLabelTemplates({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getLabelTemplates>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}

/**
 * @summary Search Label Templates
 */
export interface SearchLabelsBody {
    template_type: string;
    site_code: string;
    crop_code: string;
}

export const searchLabels = (data: SearchLabelsBody, options?: AxiosRequestConfig): Promise<AxiosResponse<LabelTemplate[]>> => {
    return axios.post(`/api/v1/search-labels`, data, options)
}

export type SearchLabelsMutationResult = NonNullable<Awaited<ReturnType<typeof searchLabels>>>
export type SearchLabelsMutationBody = SearchLabelsBody
export type SearchLabelsMutationError = AxiosError<HTTPValidationError>

export const useSearchLabels = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof searchLabels>>, TError, { data: SearchLabelsBody }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchLabels>>, { data: SearchLabelsBody }> = (props) => {
        const { data } = props ?? {}
        return searchLabels(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof searchLabels>>, TError, { data: SearchLabelsBody }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Validate Material ID for Label Printing
 */
export const validateMatIdForLabelPrinting = (
    data: { matid: string; created_by: number },
    options?: AxiosRequestConfig
): Promise<AxiosResponse<any>> => {
    return axios.post(`/api/v1/label-printing-matid`, data, options);
};

export const useValidateMatIdForLabelPrinting = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof validateMatIdForLabelPrinting>>, TError, { data: { matid: string; created_by: number } }, TContext>;
    axios?: AxiosRequestConfig;
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};
    const mutationFn: MutationFunction<Awaited<ReturnType<typeof validateMatIdForLabelPrinting>>, { data: { matid: string; created_by: number } }> = (props) => {
        const { data } = props ?? {};
        return validateMatIdForLabelPrinting(data, axiosOptions);
    };

    return useMutation<Awaited<ReturnType<typeof validateMatIdForLabelPrinting>>, TError, { data: { matid: string; created_by: number } }, TContext>(
        mutationFn,
        mutationOptions
    );
};

/**
 * @summary  createanother-harvest
 */
export const createAnotherHarvest  = (data: CreateMultipleHarvestModel, options?: AxiosRequestConfig): Promise<AxiosResponse<CreateSPLotModel>> => {
    return axios.post(`/api/v1/harvests`, data, options)
}
export const useCreateAnotherHarvest= <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof createAnotherHarvest>>, TError, { data: CreateMultipleHarvestModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAnotherHarvest>>, { data: CreateMultipleHarvestModel }> = (props) => {
        const { data } = props ?? {}

        return createAnotherHarvest(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof createAnotherHarvest>>, TError, { data: CreateMultipleHarvestModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

export const getWorkflowProtocols = (options?: AxiosRequestConfig): Promise<AxiosResponse<LabelTemplate[]>> => {
    return axios.get(`/api/v1/workflow-steps`, options)
}

export const getWorkflowProtocolsQueryKey = () => [`/api/v1/workflow-steps`]


export const useGetWorkflowProtocols = <
    TData = Awaited<ReturnType<typeof getWorkflowProtocols>>,
    TError = AxiosError<unknown>,
>(options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getWorkflowProtocols>>, TError, TData>
    axios?: AxiosRequestConfig
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {}

    const queryKey = queryOptions?.queryKey ?? getWorkflowProtocolsQueryKey()

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowProtocols>>> = ({ signal }) =>
        getWorkflowProtocols({ signal, ...axiosOptions })

    const query = useQuery<Awaited<ReturnType<typeof getWorkflowProtocols>>, TError, TData>(
        queryKey,
        queryFn,
        queryOptions
    ) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

    query.queryKey = queryKey

    return query
}


/* trial-materials   */



/**
 * @summary Search Trials
 */
export const inSeasonSearchTrials = (
    trialSearchParams: SearchTrialsModel,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<TrialMaterialModel[]>> => {
    return axios.post(`/api/v1/search-trials`, trialSearchParams, options)
}

export type InSeasonSearchTrialsResult = NonNullable<Awaited<ReturnType<typeof searchTrials>>>
export type InSeasonSearchTrialsMutationBody = SearchTrialsModel
export type InSeasonSearchTrialsMutationError = AxiosError<HTTPValidationError>

export const UseInSeasonSearchTrials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof inSeasonSearchTrials>>,
        TError,
        { data: SearchTrialsModel },
        TContext
    >
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof inSeasonSearchTrials>>, { data: SearchTrialsModel }> = (
        props
    ) => {
        const { data } = props ?? {}

        return inSeasonSearchTrials(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof inSeasonSearchTrials>>, TError, { data: SearchTrialsModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}


/* View In Season Trials */
export const getTrialsForBreeder = (reqObj: getInseasonTrialObj, options?: AxiosRequestConfig): Promise<AxiosResponse<SPLotModel>> => {
    const params = new URLSearchParams({
        site_code: reqObj.siteCode,
        page: String(reqObj.page + 1),
        page_size: String(reqObj.pageSize),
        ...(reqObj.sort_by && { sort_by: reqObj.sort_by }),
        ...(reqObj.sort_order && { sort_order: reqObj.sort_order }),
    });
    return axios.get(`/in-season-api/v1/get-trials-for-breeder?${params.toString()}`, options)
}

export const useGetTrialsForBreeder = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getTrialsForBreeder>>, TError, { reqObj: getInseasonTrialObj }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getTrialsForBreeder>>, { reqObj: getInseasonTrialObj }> = (props) => {
        return getTrialsForBreeder(props.reqObj, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getTrialsForBreeder>>, TError, { reqObj: getInseasonTrialObj }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/**
 * @summary Move materials to another SP Lot
 */
export const moveMaterialsToSplot = (
    moveMaterialsToSplotModel: import('./SPLotModel').MoveMaterialsToSplotModel,
    options?: AxiosRequestConfig
): Promise<AxiosResponse> => {
    return axios.post(`/api/v1/materials-move-to-splot`, moveMaterialsToSplotModel, options)
}

export type MoveMaterialsToSplotMutationResult = NonNullable<Awaited<ReturnType<typeof moveMaterialsToSplot>>>;
export type MoveMaterialsToSplotMutationBody = import('./SPLotModel').MoveMaterialsToSplotModel;
export type MoveMaterialsToSplotMutationError = AxiosError<HTTPValidationError>;

export const useMoveMaterialsToSplot = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof moveMaterialsToSplot>>, TError, { data: import('./SPLotModel').MoveMaterialsToSplotModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof moveMaterialsToSplot>>, { data: import('./SPLotModel').MoveMaterialsToSplotModel }> = (props) => {
        const { data } = props ?? {}
        return moveMaterialsToSplot(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof moveMaterialsToSplot>>, TError, { data: import('./SPLotModel').MoveMaterialsToSplotModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/* drill_down_splot_by_id  */

export const getInTrialMaterialsById = (reqObj: any, options?: AxiosRequestConfig): Promise<AxiosResponse<SPLotModel>> => {
    return axios.get(`/in-season-api/v1/trial-materials?trial_id=${reqObj.trial_bid}`, options)
}

export const useGetInTrialMaterialsById= <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof getInTrialMaterialsById>>, TError, { reqObj: import('./inSeasonApiModel').getInseasonTrialMaterialObj }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof getInTrialMaterialsById>>, { reqObj: import('./inSeasonApiModel').getInseasonTrialMaterialObj }> = (props) => {
        return getInTrialMaterialsById(props.reqObj, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof getInTrialMaterialsById>>, TError, { reqObj: import('./inSeasonApiModel').getInseasonTrialMaterialObj }, TContext>(
        mutationFn,
        mutationOptions
    )
}

/* Insert Trial Materials */
export const insertTrialMaterials = (
    data: import('./inSeasonApiModel').InsertTrialMaterialsModel,
    options?: AxiosRequestConfig
): Promise<AxiosResponse> => {
    return axios.post(`/in-season-api/v1/submit_modified_trials`, data, options)
}

export const useInsertTrialMaterials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof insertTrialMaterials>>, TError, { data: import('./inSeasonApiModel').InsertTrialMaterialsModel }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof insertTrialMaterials>>, { data: import('./inSeasonApiModel').InsertTrialMaterialsModel }> = (props) => {
        const { data } = props ?? {}
        return insertTrialMaterials(data, axiosOptions)
    }

    return useMutation<Awaited<ReturnType<typeof insertTrialMaterials>>, TError, { data: import('./inSeasonApiModel').InsertTrialMaterialsModel }, TContext>(
        mutationFn,
        mutationOptions
    )
}


/**
 * @summary Search  Harvested Data
 */
export const searchInSeasonTrials = (InSeasonModel: Record<string, any>, url: string, options?: AxiosRequestConfig): Promise<AxiosResponse> => {
    
    return axios.post(`${url}`, InSeasonModel, options)
}

export const useSearchInSeasonTrials = <TError = AxiosError<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof searchInSeasonTrials>>, TError, { data: Record<string, any>, url: string }, TContext>
    axios?: AxiosRequestConfig
}) => {
    const { mutation: mutationOptions, axios: axiosOptions } = options ?? {}

    const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchInSeasonTrials>>, { data: Record<string, any>, url: string }> = (props) => {
        const { data, url } = props ?? {}

        return searchInSeasonTrials(data, url, axiosOptions)
    }
    return useMutation<Awaited<ReturnType<typeof searchInSeasonTrials>>, TError, { data: Record<string, any>, url: string }, TContext>(
        mutationFn,
        mutationOptions
    )
}